/**
 * Black box recorder - survives a force-quit so the NEXT incident is a
 * diagnosis instead of another investigation.
 *
 * WHY THIS EXISTS. Three sessions have now tried to answer one question from
 * outside the iPad and failed: when the rig "froze" mid-gig, WAS THE
 * JAVASCRIPT STILL RUNNING? Everything downstream depends on it, and the two
 * answers need opposite fixes:
 *
 *   alive  -> the app lost its bridge and correctly greyed every control out
 *             (FireButton is `disabled={!live()}`, DEC-124 Clause A). Nothing
 *             is wrong with the app; the network went and the UI failed to
 *             SAY so. A restart "fixes" it only by coincidence of timing.
 *   dead   -> iOS suspended or killed the WebKit content process. A restart
 *             genuinely is the fix, and the work is to stop it being killed.
 *
 * Measuring from the bridge cannot separate them, which is the whole problem.
 * A disconnected-but-healthy app issues ZERO requests, because clockPoll
 * returns early without fetching when no primary is connected - the identical
 * reading to a dead JS context. That ambiguity is what led the 2026-09-08
 * investigation to conclude "its JS was not executing at all" on evidence
 * that does not actually support it.
 *
 * HOW IT ANSWERS THE QUESTION. `up` is a counter incremented by a timer. It
 * can only advance while the JS main thread is running. After an incident:
 *
 *   ticks CONTINUE across it, `conn` flips to 0 and stays there
 *     -> the app was alive throughout. Network fault.
 *   ticks STOP dead, and the next run's first sample is minutes later
 *     -> the JS context was gone. iOS killed or suspended it.
 *
 * The gap between the last sample of the previous run and the first of the
 * new one IS the measurement. Nothing has to be inferred.
 *
 * THE PREVIOUS RUN IS PRESERVED ON BOOT. Without that, the force-quit that
 * ends an incident also destroys the only record of it, and this would be a
 * check that can never report the thing it exists for.
 *
 * WHAT THE RING CANNOT DO, AND WHY THERE IS A SECOND LOG. The ring holds
 * THREE HOURS (1080 samples at 10s), and `previous` is overwritten on EVERY
 * boot. Both are correct for the job above, which is read DURING or right
 * after an incident: the newest end is the incident, and the run before this
 * one is the run that contains it.
 *
 * They are both wrong for the other question this recorder has been asked to
 * answer since CP-132 - *"after the next gig, whatever happens, read the black
 * box"*. A five hour set overflows the ring, and by the time the app has been
 * opened twice `previous` holds the drive home rather than the gig. So the
 * instruction and the instrument disagreed, and nothing compared them until
 * 2026-09-12.
 *
 * `KEY_EVENTS` is the answer, and it is deliberately NOT a longer ring. It
 * records only TRANSITIONS - the moments, not the timeline - plus one entry
 * per boot, and it is never wiped. A gig's drop history therefore survives
 * ring rollover, a force-quit, and any number of later app starts.
 *
 * The BOOT entry carries the ending run's last wallclock and heartbeat count,
 * so the JS-alive gap above stays computable from the durable log alone, long
 * after the ring that produced it has been recycled. It is DERIVED from the
 * ring at boot rather than measured separately, so the two can never disagree.
 *
 * COST. One compact line per sample into a bounded ring, written as a single
 * string. At 10s / 1080 entries that is ~3 hours of history in roughly 40KB,
 * rewritten once per sample - well under a millisecond. Every storage touch
 * is wrapped, because a recorder that can take the app down would be a
 * diagnostic that causes incidents. It touches no dispatch, poll or reconnect
 * path.
 */

import { watchdogVerdict, exportWatchdog } from './watchdog.ts';

const KEY_CURRENT = 'opbuttons.blackbox.current.v1';
const KEY_PREVIOUS = 'opbuttons.blackbox.previous.v1';

/** 10s: fine enough to place an event to within one sample, coarse enough
 *  that a 3-hour history fits one bounded ring. */
const SAMPLE_MS = 10_000;
/** 1080 x 10s = 3 hours. A 5-hour set overflows the oldest end, which is the
 *  right end to lose: the incident is always nearest the newest. */
const MAX_ENTRIES = 1080;

/** The durable transition log. Separate key on purpose: the ring is recycled
 *  and re-copied on every boot, and this must survive both. */
const KEY_EVENTS = 'opbuttons.blackbox.events.v1';

/* THE TWO CAPS, and both numbers are derived rather than round.
 *
 * COUNT. CP-132's worst observed night was seven self-healing WebSocket drops
 * plus the bridge loss that ended it, so call a bad gig 40 transitions and 3
 * app starts. 800 entries is therefore about 18 bad gigs, and at 3 gigs a week
 * that is roughly six weeks of history. That is the floor this is set against:
 * the requirement is "still there when the gig is read the next day", and six
 * weeks clears it by a wide margin.
 *
 * BYTES. The count cap alone is not a storage bound, because a detail string
 * could grow. This is an iPad sharing a ~5MB origin quota with the operator's
 * authored projects, and a diagnostic that evicts a gig page would be a far
 * worse bug than the one it diagnoses. 48KB is ~1% of that quota and about
 * the same size as one of the two ring keys that already live here.
 *
 * KNOWN AND ACCEPTED: a bridge flapping every sample would fill the log in a
 * couple of hours and evict older gigs. That is the right trade - a night that
 * flapped 800 times IS the incident, and it is the newest end that is kept.
 */
const MAX_EVENTS = 800;
const MAX_EVENT_BYTES = 48_000;
/** Details are labels, not payloads. Bounded so one long string cannot eat
 *  the byte cap and evict real history behind it. */
const MAX_DETAIL = 80;

export interface BlackBoxSample {
  /** Wall clock, ms. Jumps forward across a suspend; `up` does not. */
  t: number;
  /** Monotonic tick count. Advances ONLY while JS is executing. */
  up: number;
  /** Any bridge connected. */
  conn: boolean;
  /** Consumer WebSocket open. */
  ws: boolean;
  /** Bridges configured, and how many of them are connected. */
  total: number;
  ok: number;
  /** Short note marking a transition worth keeping. */
  note: string;
}

/** One durable entry. Kinds: BOOT, BRIDGE-LOST, BRIDGE-UP, WS-LOST, WS-UP. */
export interface BlackBoxEvent {
  /** Wall clock, ms. */
  t: number;
  /** Heartbeat count at the moment it happened. 0 on a BOOT, by definition. */
  up: number;
  kind: string;
  detail: string;
}

export interface BlackBoxState {
  conn: boolean;
  ws: boolean;
  total: number;
  ok: number;
}

let tick = 0;
let ring: string[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let started = false;
let lastConn: boolean | null = null;
let lastWs: boolean | null = null;
let onVisibility: (() => void) | null = null;
let onPageHide: ((e: PageTransitionEvent) => void) | null = null;

/** Ring notes for the page's own last words (see `startBlackBox`). */
const NOTE_HIDDEN = 'HIDDEN';
const NOTE_SHOWN = 'SHOWN';
const NOTE_CLOSED = 'CLOSED';

function encode(s: BlackBoxSample): string {
  const note = s.note.replace(/[,\n]/g, ' ');
  return [s.t, s.up, s.conn ? 1 : 0, s.ws ? 1 : 0, s.total, s.ok, note].join(',');
}

function decode(line: string): BlackBoxSample | null {
  const p = line.split(',');
  if (p.length < 7) return null;
  return {
    t: Number(p[0]),
    up: Number(p[1]),
    conn: p[2] === '1',
    ws: p[3] === '1',
    total: Number(p[4]),
    ok: Number(p[5]),
    note: p.slice(6).join(','),
  };
}

function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* quota, private mode, disabled */ }
}

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function record(note: string, st: BlackBoxState): void {
  ring.push(encode({ t: Date.now(), up: tick, conn: st.conn, ws: st.ws, total: st.total, ok: st.ok, note }));
  if (ring.length > MAX_ENTRIES) ring.splice(0, ring.length - MAX_ENTRIES);
  safeSet(KEY_CURRENT, ring.join('\n'));
}

/**
 * Append to the DURABLE log. Read-modify-write rather than kept in memory,
 * because two page contexts can be open at once (the operator's iPad and a
 * laptop tab share nothing but this key) and the in-memory copy of whichever
 * wrote last would otherwise silently truncate the other's history.
 *
 * Eviction is from the FRONT and honours BOTH caps, because either one alone
 * is a hole: the count cap does not bound bytes, and the byte cap alone would
 * let a stream of tiny entries grow the array without limit.
 */
function appendEvent(kind: string, detail: string): void {
  const clean = detail.replace(/[,\n]/g, ' ').slice(0, MAX_DETAIL);
  const raw = safeGet(KEY_EVENTS);
  const lines = raw ? raw.split('\n').filter((l) => l !== '') : [];
  lines.push([Date.now(), tick, kind, clean].join(','));
  while (lines.length > MAX_EVENTS) lines.shift();
  let joined = lines.join('\n');
  while (joined.length > MAX_EVENT_BYTES && lines.length > 1) {
    lines.shift();
    joined = lines.join('\n');
  }
  safeSet(KEY_EVENTS, joined);
}

/** The durable transition log, oldest first. Survives boots and ring rollover. */
export function readBlackBoxEvents(): BlackBoxEvent[] {
  const raw = safeGet(KEY_EVENTS);
  if (!raw) return [];
  return raw.split('\n').flatMap((line) => {
    const p = line.split(',');
    if (p.length < 4) return [];
    return [{ t: Number(p[0]), up: Number(p[1]), kind: p[2]!, detail: p.slice(3).join(',') }];
  });
}

/**
 * The durable log in one sentence, for the same reason `blackBoxVerdict` is
 * one: an iPad has no console, and a wall of CSV asks the operator to do the
 * analysis at exactly the moment they are least able to.
 *
 * It leads with the WINDOW ("since <date>") rather than the counts, because
 * "0 bridge drops" is only meaningful alongside how far back that looks. A
 * log that started this morning reporting zero drops would otherwise read as
 * a clean bill of health for last night's gig.
 */
export function blackBoxHistorySummary(): string {
  const ev = readBlackBoxEvents();
  if (ev.length === 0) return 'No drop history recorded yet.';
  const count = (k: string): number => ev.filter((e) => e.kind === k).length;
  const drops = count('BRIDGE-LOST');
  const wsDrops = count('WS-LOST');
  const boots = count('BOOT');
  const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;
  const last = ev[ev.length - 1]!;
  return `Since ${new Date(ev[0]!.t).toLocaleDateString()}: `
    + `${plural(drops, 'bridge drop')}, ${plural(wsDrops, 'socket drop')}, `
    + `${plural(boots, 'app start')}. `
    + `Last entry ${last.kind} at ${new Date(last.t).toLocaleString()}.`;
}

/**
 * Start recording. Idempotent.
 *
 * `readState` is injected rather than imported so this module keeps zero app
 * dependencies and can never contribute to an import cycle in the very
 * connection code it is watching.
 */
export function startBlackBox(readState: () => BlackBoxState): void {
  if (started) return;
  started = true;

  // Preserve the previous run BEFORE touching the current key. The force-quit
  // that ends an incident is followed immediately by this boot, so this is the
  // line that keeps the evidence alive.
  const prev = safeGet(KEY_CURRENT);
  if (prev) safeSet(KEY_PREVIOUS, prev);
  ring = [];

  // The BOOT entry, DERIVED from the ring that just ended rather than measured
  // independently, so the durable log and the ring can never tell two stories.
  // This is the line that keeps the JS-alive gap answerable after `previous`
  // has been overwritten by later app starts - which, on a rig that gets
  // reopened, is by the next morning.
  const prevRows = prev
    ? prev.split('\n').map(decode).filter((x): x is BlackBoxSample => x !== null)
    : [];
  const lastPrev = prevRows[prevRows.length - 1];
  appendEvent(
    'BOOT',
    lastPrev
      ? `${Math.round((Date.now() - lastPrev.t) / 1000)}s gap after ${lastPrev.up} heartbeats`
      : 'no previous run on this device',
  );

  const first = readState();
  lastConn = first.conn;
  lastWs = first.ws;
  record('session-start', first);

  // The page's own last words (2026-10-18). A frozen main thread can fire no
  // event at all, so a run that ENDS on one of these notes did not freeze: it
  // was closed or reloaded (CLOSED), or the system paused it in the background
  // (HIDDEN; iOS does it to a backgrounded app, a desktop browser to a hidden
  // tab). Before this, all three read as "JS STOPPED" - a report from a closed
  // desktop tab did exactly that (help desk d1ab295b, 2026-10-08).
  onVisibility = () => record(document.visibilityState === 'hidden' ? NOTE_HIDDEN : NOTE_SHOWN, readState());
  // `persisted` = kept in memory to resume (the back-forward cache, which iOS
  // can use for a suspended app): a pause, not a close.
  onPageHide = (e: PageTransitionEvent) => record(e.persisted ? NOTE_HIDDEN : NOTE_CLOSED, readState());
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  timer = setInterval(() => {
    tick += 1;
    const st = readState();
    // Name transitions explicitly so a reader does not have to diff columns.
    const connChanged = lastConn !== null && st.conn !== lastConn;
    const wsChanged = lastWs !== null && st.ws !== lastWs;
    let note = '';
    if (connChanged) note = st.conn ? 'BRIDGE-UP' : 'BRIDGE-LOST';
    else if (wsChanged) note = st.ws ? 'WS-UP' : 'WS-LOST';
    // The durable log records BOTH, where the ring's single `note` can only
    // name one. A sample where the bridge AND the socket flip together is
    // precisely the total-outage shape, so it is the one case where the ring
    // is guaranteed to under-report and the durable log must not.
    if (connChanged) {
      appendEvent(st.conn ? 'BRIDGE-UP' : 'BRIDGE-LOST', `${st.ok}/${st.total} bridges connected`);
    }
    if (wsChanged) {
      appendEvent(st.ws ? 'WS-UP' : 'WS-LOST', `${st.ok}/${st.total} bridges connected`);
    }
    lastConn = st.conn;
    lastWs = st.ws;
    record(note, st);
  }, SAMPLE_MS);
}

export function stopBlackBox(): void {
  if (timer !== null) { clearInterval(timer); timer = null; }
  if (onVisibility) document.removeEventListener('visibilitychange', onVisibility);
  if (onPageHide) window.removeEventListener('pagehide', onPageHide);
  onVisibility = onPageHide = null;
  started = false;
}

/** How the run ended, from the last visibility / close note in it: the run's
 *  state at the moment its ticks stopped. Scanned back rather than read off the
 *  final sample, because a hidden desktop tab keeps ticking (throttled) after
 *  its HIDDEN note. A run from a build before these notes reads 'shown'. */
function endState(rows: BlackBoxSample[]): 'closed' | 'hidden' | 'shown' {
  for (let i = rows.length - 1; i >= 0; i--) {
    const n = rows[i]!.note;
    if (n === NOTE_CLOSED) return 'closed';
    if (n === NOTE_HIDDEN) return 'hidden';
    if (n === NOTE_SHOWN || n === 'session-start') return 'shown';
  }
  return 'shown';
}

/** Parsed samples. Defaults to the run BEFORE this one, because that is the
 *  run that contains the incident. */
export function readBlackBox(which: 'current' | 'previous' = 'previous'): BlackBoxSample[] {
  const raw = safeGet(which === 'current' ? KEY_CURRENT : KEY_PREVIOUS);
  if (!raw) return [];
  return raw.split('\n').map(decode).filter((x): x is BlackBoxSample => x !== null);
}

/**
 * The verdict in one sentence, computed rather than eyeballed.
 *
 * Compares the last sample of the previous run against the first of the
 * current one. A gap materially larger than SAMPLE_MS means the JS was not
 * running in between. A normal gap, with the ring showing a long
 * disconnected tail, means it was alive and merely cut off.
 */
export function blackBoxVerdict(): string {
  const prev = readBlackBox('previous');
  const cur = readBlackBox('current');
  if (prev.length === 0) return 'No previous run recorded yet.';

  const last = prev[prev.length - 1]!;
  const first = cur[0];

  let offlineTail = 0;
  for (let i = prev.length - 1; i >= 0 && !prev[i]!.conn; i--) offlineTail++;
  const offlineMin = Math.round((offlineTail * SAMPLE_MS) / 60000);

  if (!first) {
    return `Previous run ended ${new Date(last.t).toLocaleString()} after ${last.up} heartbeats.`;
  }

  const gapMs = first.t - last.t;
  const gapMin = Math.round(gapMs / 60000);

  if (gapMs > SAMPLE_MS * 3) {
    const end = endState(prev);
    if (end === 'closed') {
      return `PAGE CLOSED. The page said it was closing or reloading at ${new Date(last.t).toLocaleTimeString()}, `
        + `and the next run began ${new Date(first.t).toLocaleTimeString()} (${gapMin} min later). `
        + `A close, reload or quit, not a freeze.`;
    }
    if (end === 'hidden') {
      return `PAUSED IN THE BACKGROUND. The app went to the background and no heartbeat followed until `
        + `the next run began ${new Date(first.t).toLocaleTimeString()} (last heartbeat `
        + `${new Date(last.t).toLocaleTimeString()}, ${gapMin} min gap). The system paused or closed it there `
        + `(iOS does this to a backgrounded app, a desktop browser to a hidden tab), so it did not freeze on screen.`;
    }
    return `JS STOPPED. Last heartbeat ${new Date(last.t).toLocaleTimeString()}, next run began `
      + `${new Date(first.t).toLocaleTimeString()} - a ${gapMin} min gap with no ticks at all. `
      + `The page was not running, and it was not a network fault. A page stuck in its own code and `
      + `iOS stopping the app look the same here. `
      // Until 2026-10-08 this said "That is an iOS suspend or kill" - a guess
      // the heartbeat cannot make. The 2026-10-07 freeze (screen frozen, no
      // touch, socket open) read as exactly that and pointed the wrong way.
      + watchdogVerdict();
  }
  if (offlineTail > 1) {
    return `JS STAYED ALIVE. Heartbeats continued right to the end (${last.up} of them), but the `
      + `bridge was disconnected for the last ~${offlineMin} min. The app was working and had lost `
      + `its link, so the restart is not what fixed it.`;
  }
  return `Previous run ended cleanly while connected (${last.up} heartbeats, last at `
    + `${new Date(last.t).toLocaleTimeString()}). No incident recorded.`;
}

/** Everything as text, for pasting into a bug report. */
export function exportBlackBox(): string {
  const fmt = (label: string, rows: BlackBoxSample[]): string =>
    `\n--- ${label} (${rows.length} samples) ---\n`
    + rows.map((r) => [
      new Date(r.t).toISOString(), r.up, r.conn ? 1 : 0, r.ws ? 1 : 0, r.total, r.ok, r.note,
    ].join(',')).join('\n');

  const events = readBlackBoxEvents();
  const eventBlock = `\n--- durable transition log (${events.length} entries) ---\n`
    + events.map((e) => [new Date(e.t).toISOString(), e.up, e.kind, e.detail].join(',')).join('\n');

  return 'OPButtons black box\n'
    + `verdict: ${blackBoxVerdict()}\n`
    + `history: ${blackBoxHistorySummary()}\n\n`
    // Stated here rather than in the Settings row, because this is the text
    // that reaches a bug report and the caveat is load-bearing there: the log
    // is per-device and per-install. A gig played on a different device, or
    // before this build shipped, is genuinely absent - not quiet.
    + 'The durable log is this device only, and starts at the first entry below.\n'
    + 'An incident on another device, or before this build, will not appear here.\n\n'
    + 'columns: wallclock,heartbeats,bridgeConnected,wsOpen,bridgesTotal,bridgesOk,note\n'
    + fmt('previous run', readBlackBox('previous'))
    + fmt('current run', readBlackBox('current'))
    + '\n\ncolumns: wallclock,heartbeats,kind,detail\n'
    + eventBlock
    + '\n'
    + exportWatchdog();
}
